import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import Dropdown from "react-bootstrap/Dropdown";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import { useSortAndFilter } from "../contexts/SortAndFilterContext";
import {
  Container,
  MenuContainer,
  OptionContainer,
  StyledCheckbox,
  StyledCol,
  StyledDropdown,
  StyledFontAwesomeIcon,
} from "./styles";
import { messages } from "./messages";
import { getDefaultFields } from "../shared/helper";

const transformArrayIn2Dimension = (array: any[]) => {
  const arr = [...array];
  arr.splice(0, 1)
  return arr.reduce((rows: any, key: any, index: number) =>
    (index % 2 === 0 ? rows.push([key]) : rows[rows.length - 1].push(key)) && rows, [[array[0]]]);
};

interface DropdownFilterProps {
  name: string;
  title: string;
  options: { label: string, value: string, isChecked?: boolean }[];
}

export const DropdownFilter = (props: DropdownFilterProps): JSX.Element => {
  const intl = useIntl();
  const sortAndFilterContext = useSortAndFilter();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);
  const [options, setOptions] = useState<{ label: string, value: string }[]>(props.options);

  const isDisabled = useMemo(() =>
    props.name === "fields" && selectedOptions.length === 1,
    [props, selectedOptions]
  );

  const setSelectedOptionsWithContextData = useCallback((contextData: any) => {
    setSelectedOptions(contextData);
  }, []);

  const setOptionsWithContextData = useCallback((contextData: any) => {
    setOptions(
      props.options.map((it: any) => {
        const isChecked = contextData.find((stored: any) => stored.value === it.value);
        return { ...it, isChecked: !!isChecked };
      }),
    );
  }, [props.options]);

  const setSelectedOptionsWithCheckedFlag = useCallback(() => {
    setSelectedOptions(props.options.filter((it) => it.isChecked));
  }, [props.options]);

  const setOptionsWithCheckedFlag = useCallback(() => {
    setOptions(props.options.map((it) => ({ ...it, isChecked: it.isChecked || false })));
  }, [props.options]);

  const setSelectedOptionsToAll = useCallback(() => {
    setSelectedOptions(props.options);
  }, [props.options]);

  const setSelectedOptionsToNone = useCallback(() => {
    const result = props.name === "fields" ? getDefaultFields(intl) : [];
    setSelectedOptions(result);
  }, [props.name, intl]);

  const setOptionsToAllChecked = useCallback(() => {
    setOptions(props.options.map((it: any) => ({ ...it, isChecked: true })));
  }, [props.options]);

  const setOptionsToAllUnchecked = useCallback(() => {
    const result = props.name === "fields"
      ? getDefaultFields(intl)
      : props.options.map((it: any) => ({ ...it, isChecked: false }));
    setOptions(result);
  }, [props.options, props.name, intl]);

  const sendAllSelectedOptionsToContext = useCallback(() => {
    sortAndFilterContext.storeFilters({ [props.name]: props.options });
  }, [sortAndFilterContext, props.options, props.name]);

  const sendNoneSelectedOptionsToContext = useCallback(() => {
    const result = props.name === "fields" ? { fields: getDefaultFields(intl) } : { [props.name]: [] };
    sortAndFilterContext.storeFilters(result);
  }, [sortAndFilterContext, props.name, intl]);

  const sendSelectedOptionsToContext = useCallback((options: any) => {
    sortAndFilterContext.storeFilters({ [props.name]: options });
  }, [sortAndFilterContext, props.name]);

  const selectedValueAlreadyExistsOnSelectedOptions = useCallback((value: any) => {
    return selectedOptions.find((it) => it.value === value);
  }, [selectedOptions]);

  const setOptionsAsCheckWithSelectedValue = useCallback((value: any, isChecked: boolean) => {
    setOptions(options.map((it: any) => ({
      ...it,
      isChecked: it.value === value ? isChecked : (it.isChecked || false),
    })));
  }, [options]);

  useEffect(() => {
    const storedFilters: any = sortAndFilterContext.getFilters();
    if (storedFilters && storedFilters[props.name]) {
      const contextData = storedFilters[props.name];
      setSelectedOptionsWithContextData(contextData);
      setOptionsWithContextData(contextData);
    } else {
      setSelectedOptionsWithCheckedFlag();
      setOptionsWithCheckedFlag();
    }
  }, [
    props,
    sortAndFilterContext,
    setSelectedOptionsWithContextData,
    setOptionsWithContextData,
    setSelectedOptionsWithCheckedFlag,
    setOptionsWithCheckedFlag,
  ]);

  const handleOnTogle = useCallback((event: any) => {
    setIsOpen(event);
  }, []);

  const handleSelectAll = useCallback((event: any) => {
    setSelectedOptionsToAll();
    setOptionsToAllChecked();
    sendAllSelectedOptionsToContext();
  }, [setSelectedOptionsToAll, setOptionsToAllChecked, sendAllSelectedOptionsToContext]);

  const handleUnselectAll = useCallback((event: any) => {
    setSelectedOptionsToNone();
    setOptionsToAllUnchecked();
    sendNoneSelectedOptionsToContext();
  }, [setSelectedOptionsToNone, setOptionsToAllUnchecked, sendNoneSelectedOptionsToContext]);

  const handleOnChange = useCallback((event: any, item: any) => {
    const targetValue = event.target.value;
    if (selectedValueAlreadyExistsOnSelectedOptions(targetValue)) {
      const options = selectedOptions.filter((it) => it.value !== targetValue);
      setSelectedOptions(options);
      sendSelectedOptionsToContext(options);
    } else {
      setSelectedOptions([...selectedOptions, item]);
      sendSelectedOptionsToContext([...selectedOptions, item]);
    }

    setOptionsAsCheckWithSelectedValue(targetValue, event.target.checked);
  }, [
    selectedOptions,
    sendSelectedOptionsToContext,
    selectedValueAlreadyExistsOnSelectedOptions,
    setOptionsAsCheckWithSelectedValue,
  ]);

  const selectedOptionsLength = useMemo((): string =>
      selectedOptions.length ? ` (${selectedOptions.length})` : "",
    [selectedOptions]
  );

  return (
    <Container>
      <StyledDropdown
        role="dropdown"
        className={selectedOptions.length > 0 ? "option-selected" : ""}
        onToggle={handleOnTogle}
      >
        <Dropdown.Toggle id={`dropdown-${props.name}`}>
          {props.title}{selectedOptionsLength}
          <StyledFontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <MenuContainer>
            {props.name !== "fields" && options.map((option: any, index: number) => (
              <OptionContainer key={option.label}>
                <StyledCheckbox
                  inline
                  type="checkbox"
                  label={option.label}
                  id={option.label}
                  onChange={(event: any) => handleOnChange(event, option)}
                  value={option.value}
                  checked={option.isChecked}
                  disabled={option.isChecked && isDisabled}
                />
              </OptionContainer>
            ))}

            {props.name === "fields" && transformArrayIn2Dimension(options).map(([left, right]: any, index: number) => (
              <Row key={index}>
                <StyledCol>
                  <StyledCheckbox
                    inline
                    type="checkbox"
                    label={left.label}
                    id={left.label}
                    onChange={(event: any) => handleOnChange(event, left)}
                    value={left.value}
                    checked={left.isChecked}
                    disabled={left.isChecked && isDisabled}
                  />
                </StyledCol>
                {right && <StyledCol>
                  <StyledCheckbox
                    inline
                    type="checkbox"
                    label={right.label}
                    id={right.label}
                    onChange={(event: any) => handleOnChange(event, right)}
                    value={right.value}
                    checked={right.isChecked}
                    disabled={right.isChecked && isDisabled}
                  />
                </StyledCol>}
              </Row>
            ))}
            <Row xs={2}>
              <Col>
                <Button variant="link" onClick={handleSelectAll}>
                  {intl.formatMessage(messages.selectAll)}
                </Button>
              </Col>
              <Col>
                <Button variant="link" onClick={handleUnselectAll}>
                  {intl.formatMessage(messages.unSelectAll)}
                </Button>
              </Col>
            </Row>
          </MenuContainer>
        </Dropdown.Menu>

      </StyledDropdown>
    </Container>
  );
};
